import { Button } from '@mui/material';
import pluralize from 'pluralize';
import * as React from 'react';
import { Link } from 'react-router-dom';
import PerDiemDownloadModal from 'components/PerDiemDownloadModal';
import { useUserContext } from 'common/contexts/UserContext';
import { Routes } from 'constants/pages';
import { Container, PerDiemReportStatus } from 'types/Watchlist';
import { Permission, useHasPermission } from 'utils/permissions';
import { PerDiemReportStatusesContext } from '../context';
import { EmptyCell } from './EmptyCell';
import { CellProps } from './types';
import { Cell } from './Cell';
import { parseGateDates } from './utility';

interface PerDiemReportOpen {
  open: true;
  teamMemberEmails: string[];
  containers: Container[];
  portCollectionCode: string;
}
interface PerDiemReportClosed {
  open: false;
}
type PerDiemReportState = PerDiemReportOpen | PerDiemReportClosed;
export const DetentionCell: React.FC<CellProps> = (props) => {
  const container: Container = props.row;
  const { user } = useUserContext();
  const [perDiemReport, setPerDiemReport] = React.useState<PerDiemReportState>({ open: false });
  const { containerNumber, shippingLine, containerType } = container;

  const { alert, days } = parseGateDates(container);

  const { reportStatuses, refetch: refectPerDiemReportStatus } = React.useContext(
    PerDiemReportStatusesContext
  );
  const openModal: React.MouseEventHandler<HTMLAnchorElement> = React.useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!container?.gatedOut?.portCollectionCode) {
        return;
      }
      const { portCollectionCode } = container.gatedOut;
      setPerDiemReport({
        open: true,
        containers: [container],
        teamMemberEmails: user?.teamMembers.map((p) => p.email) || [],
        portCollectionCode,
      });
    },
    [container, user]
  );
  const handleOnClosePerDiemModal = React.useCallback(() => {
    setPerDiemReport({ open: false });
    if (refectPerDiemReportStatus) {
      refectPerDiemReportStatus();
    }
  }, [refectPerDiemReportStatus]);
  const reportStatus: PerDiemReportStatus | undefined = reportStatuses[containerNumber];
  const isPerDiemGenerating = reportStatus?.generating || false;

  const hasPerDiemAccess = useHasPermission([Permission.PerDiemArchive]);

  const canGetReport = !!shippingLine && !!containerType && days !== null && days <= 100;

  if (days === null || days === undefined || days < 0) {
    return <EmptyCell />;
  }

  const label = days === 0 ? '< 1 day' : `${days} ${pluralize('day', days)}`;

  return (
    <>
      <PerDiemDownloadModal
        isOpen={perDiemReport.open}
        currentUser={user}
        emails={perDiemReport.open ? perDiemReport.teamMemberEmails : []}
        handleOnClose={handleOnClosePerDiemModal}
        containers={perDiemReport.open ? perDiemReport.containers : []}
        portCollectionCode={perDiemReport.open ? perDiemReport.portCollectionCode : null}
        modalType="containerTracking"
      />
      <Cell className="space-y-2 text-center items-center">
        <h5 className={`${alert ? 'text-red-900' : 'text-gray'}`}>{label}</h5>
        {hasPerDiemAccess &&
          alert &&
          (canGetReport ? (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            <Button variant="per-diem" disabled={isPerDiemGenerating} onClick={openModal}>
              {isPerDiemGenerating ? 'Generating…' : 'Get Per Diem Report'}
            </Button>
          ) : (
            <Link to={Routes.PerDiemFighter}>
              <h6 className="text-blue-900 underline cursor-pointer py-1">
                Search Per Diem Archive
              </h6>
            </Link>
          ))}
      </Cell>
    </>
  );
};
DetentionCell.displayName = 'ContainerTable.DetentionCell';
